import type React from 'react';
import { cn } from '../../utils/cn.js';
import { Card } from '../tailgrids/core/card.js';

interface SettingRowProps {
  title: string;
  description?: React.ReactNode;
  /** A leading icon tile (security rows). */
  icon?: React.ReactNode;
  /** `id` for the title, so a `Toggle` or button can name itself with `aria-labelledby`. */
  titleId?: string;
  /** The control on the right: a `Toggle`, a ghost button, an outline button. */
  children?: React.ReactNode;
}

/**
 * One setting: title and description on the left, its control on the right. Template example: NextAdmin
 * `profile/notification/page.tsx` (title + toggle) and `profile/security/page.tsx` (icon tile, title, description,
 * action), and the danger-zone rows of `profile/account/page.tsx`. Stacks under `sm`.
 */
export function SettingRow({ title, description, icon, titleId, children }: SettingRowProps) {
  return (
    <div className="flex flex-col gap-4 py-5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        {icon && (
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-background-gray-secondary_alt text-icon-secondary">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <p id={titleId} className="text-sm leading-5 font-medium text-text-primary">
            {title}
          </p>
          {description && <p className="mt-1 text-xs leading-4 text-text-tertiary">{description}</p>}
        </div>
      </div>
      {children && <div className="shrink-0">{children}</div>}
    </div>
  );
}

interface SettingListProps {
  children: React.ReactNode;
  /** Draws the rows in their own card: the account page's danger zone (sign out everywhere, delete account). */
  card?: boolean;
  className?: string;
}

/** The divided stack of `SettingRow`s, optionally in a card. */
export function SettingList({ children, card = false, className }: SettingListProps) {
  const rows = <div className={cn('divide-y divide-border-secondary-alt', className)}>{children}</div>;
  return card ? <Card className="bg-transparent p-5">{rows}</Card> : rows;
}
